import { ReactElement, useEffect, useState } from 'react'
import { AsyncThunkAction, AsyncThunkConfig } from '@reduxjs/toolkit'
import { useForm } from '@tanstack/react-form'

import axios, { pluckError } from 'src/utilities/http-client'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchEligibleEvents,
  fetchRecertificationCycleCategories,
  selectEligibleEventsByRecertCycleID,
  selectEligibleEventsStatus,
  selectRecertificationCategories,
  selectRecertificationCategoriesStatus,
} from 'src/v3/profile/profile.slice'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { RecertificationCycleEvent } from 'src/v3/core/types'

type EventFormProps = {
  cycleID: string
  event?: Partial<RecertificationCycleEvent>
  refetchCycles: AsyncThunkAction<any, any, AsyncThunkConfig>
  open: boolean
  setOpen: (status: boolean) => void
}

export let EventForm = ({
  cycleID,
  event,
  refetchCycles,
  open,
  setOpen,
}: EventFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()

  let eligibleEvents = useAppSelector(
    selectEligibleEventsByRecertCycleID(cycleID),
  )
  let eligibleEventsStatus = useAppSelector(selectEligibleEventsStatus)
  let allCategories = useAppSelector(selectRecertificationCategories)
  let categoriesStatus = useAppSelector(selectRecertificationCategoriesStatus)

  let [saving, setSaving] = useState(false)

  let categories =
    allCategories[cycleID || '']?.filter(
      (cat) => cat.categoryType === 'standard',
    ) || []
  let allEvents = categories.flatMap((cat) => cat.events)
  let allEventsFiltered = allEvents.filter(
    (ev) => ev.eventID === event?.eventID,
  )

  useEffect(() => {
    if (
      cycleID &&
      (!eligibleEventsStatus[cycleID] ||
        eligibleEventsStatus[cycleID] === 'uninitialized')
    ) {
      dispatch(fetchEligibleEvents(cycleID))
    }
  }, [cycleID, eligibleEventsStatus])

  useEffect(() => {
    if (
      cycleID &&
      (!categoriesStatus[cycleID] ||
        categoriesStatus[cycleID] === 'uninitialized')
    ) {
      dispatch(fetchRecertificationCycleCategories(cycleID))
    }
  }, [cycleID, categoriesStatus])

  let form = useForm({
    defaultValues: {
      eventID: event?.eventID || '',
      categories: !allEventsFiltered.length
        ? [
            {
              id: undefined,
              category: event?.categoryID || 'unselected',
              hours:
                eligibleEvents
                  .find((ev) => ev.id === event?.eventID)
                  ?.hours?.toString() || '',
            },
          ]
        : allEventsFiltered.map((rce) => ({
            id: rce.id,
            category: rce?.categoryID || 'unselected',
            hours: rce.hours?.toString() || '',
          })),
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      let ids = value.categories.map((catJoin) => catJoin.id)
      Promise.all([
        ...allEventsFiltered
          .filter((ev) => !ids.includes(ev.id))
          .map((ev) =>
            axios.delete(`/api/profile/recertification-cycle/event/${ev.id}`),
          ),
        ...value.categories.map((cat) =>
          !cat.id
            ? axios.post<RecertificationCycleEvent>(
                `/api/profile/recertification-cycle/event`,
                {
                  eventID: value.eventID,
                  categoryID: cat.category,
                  hours: cat.hours,
                },
              )
            : axios.put<RecertificationCycleEvent>(
                `/api/profile/recertification-cycle/event`,
                {
                  id: cat.id,
                  eventID: value.eventID,
                  categoryID: cat.category,
                  hours: cat.hours,
                },
              ),
        ),
      ])
        .then(() => dispatch(fetchRecertificationCycleCategories(cycleID)))
        .then(() => dispatch(refetchCycles))
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          showError?.(
            pluckError(err, 'Error Creating Recertification Cycle Live Class'),
          )
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 600, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          Add Live Class
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography>1. Select Live Class</Typography>
            <form.Field
              name="eventID"
              validators={{
                onChange: ({ value }) =>
                  !value || value === 'unselected'
                    ? 'Event is a required field'
                    : undefined,
              }}
              listeners={{
                onChange: ({ value }) => {
                  if (form.state.values.categories.length === 1) {
                    form.setFieldValue(
                      'categories[0].hours',
                      eligibleEvents
                        .find((ev) => ev.id === value)
                        ?.hours?.toString() || '',
                    )
                  }
                },
              }}
              children={(field) => (
                <Autocomplete
                  value={eligibleEvents.find(
                    (ev) => field.state.value === ev.id,
                  )}
                  onChange={(_, value) => {
                    field.handleChange(value?.id || '')
                  }}
                  options={eligibleEvents
                    .filter(
                      (ev) =>
                        event?.eventID === ev.id ||
                        !allEvents.some((rce) => rce.eventID === ev.id),
                    )
                    .toSorted((a, b) => a.name.localeCompare(b.name))}
                  getOptionKey={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => {
                    // eslint-disable-next-line fp/no-rest-parameters
                    let { key, ...optionProps } = props
                    return (
                      <Box
                        key={key}
                        component="li"
                        {...optionProps}
                        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                      >
                        <Typography sx={{ flex: '1 1 400px' }}>
                          {option.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${option.hours} Hour${option.hours === 1 ? '' : 's'}`}
                        />
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ bgcolor: 'background.paper' }}
                      label="Live Class"
                      error={field.state.meta.errors.length > 0}
                      helperText={field.state.meta.errors.join(', ')}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                />
              )}
            />
            <form.Subscribe
              selector={(state) => state.values.eventID}
              children={(ev) => {
                let hours =
                  eligibleEvents.find((compev) => compev.id === ev)?.hours || 0
                return (
                  <form.Field
                    name="categories"
                    mode="array"
                    validators={{
                      onSubmit: ({ value }) =>
                        value.reduce(
                          (sum, c) => sum + parseFloat(c.hours),
                          0,
                        ) !== hours
                          ? `All hours must be assigned & all category hours must add up to ${hours} hours.`
                          : undefined,
                    }}
                  >
                    {(field) => (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>
                            2. Assign {hours} Hour{hours === 1 ? '' : 's'}
                          </Typography>
                          {}
                          <Button
                            color="secondary"
                            sx={{
                              mb: -0.25,
                            }}
                            size="small"
                            onClick={() => {
                              field.pushValue({
                                id: undefined,
                                category: 'unselected',
                                hours: '',
                              })
                            }}
                          >
                            Add Category
                          </Button>
                          <Button
                            color="error"
                            sx={{
                              mb: -0.25,
                            }}
                            size="small"
                            onClick={() =>
                              field.removeValue(field.state.value.length - 1)
                            }
                            disabled={field.state.value.length === 1}
                          >
                            Remove Category
                          </Button>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {field.state.value.map((_, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                              <form.Field
                                name={`categories[${i}].category`}
                                validators={{
                                  onSubmit: ({ value }) =>
                                    value === 'unselected'
                                      ? 'Category is a required field'
                                      : undefined,
                                }}
                                children={(subField) => (
                                  <FormControl
                                    fullWidth
                                    error={
                                      subField.state.meta.errors.length > 0
                                    }
                                  >
                                    <InputLabel
                                      id={`event-category-label-${i}`}
                                    >
                                      Category
                                    </InputLabel>
                                    <Select
                                      labelId={`event-category-label-${i}`}
                                      value={subField.state.value}
                                      label="Category"
                                      onChange={(e) =>
                                        subField.handleChange(e.target.value)
                                      }
                                      sx={{ bgcolor: 'background.paper' }}
                                    >
                                      <MenuItem value="unselected">
                                        Unselected
                                      </MenuItem>
                                      {categories
                                        .filter(
                                          (category) =>
                                            category.id ===
                                              subField.state.value ||
                                            !field.state.value
                                              .map((c) => c.category)
                                              .includes(category.id),
                                        )
                                        .toSorted((a, b) =>
                                          `${a.name}${a.subcategory || ''}`.localeCompare(
                                            `${b.name}${b.subcategory || ''}`,
                                          ),
                                        )
                                        .map((category) => (
                                          <MenuItem
                                            key={category.id}
                                            value={category.id}
                                          >
                                            {category.name}{' '}
                                            {category.subcategory &&
                                              `(${category.subcategory})`}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    {subField.state.meta.errors.length > 0 && (
                                      <FormHelperText>
                                        {subField.state.meta.errors.join(', ')}
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                              <form.Field
                                name={`categories[${i}].hours`}
                                validators={{
                                  onSubmit: ({ value }) =>
                                    !parseFloat(value)
                                      ? 'Hours is a required field & must be greater than 0'
                                      : undefined,
                                }}
                                children={(subField) => (
                                  <TextField
                                    sx={{ bgcolor: 'background.paper' }}
                                    fullWidth
                                    variant="outlined"
                                    value={subField.state.value}
                                    label="Hours"
                                    name={subField.name}
                                    onChange={(e) => {
                                      subField.handleChange(
                                        e.target.value.replace(/[^\d.]/g, ''),
                                      )
                                      field.validate('change')
                                    }}
                                    onBlur={subField.handleBlur}
                                    error={
                                      subField.state.meta.errors.length > 0
                                    }
                                    helperText={subField.state.meta.errors.join(
                                      ', ',
                                    )}
                                  />
                                )}
                              />
                            </Box>
                          ))}
                          {field.state.meta.errors.length > 0 && (
                            <FormHelperText sx={{ color: 'error.main' }}>
                              {field.state.meta.errors.join(', ')}
                            </FormHelperText>
                          )}
                        </Box>
                      </>
                    )}
                  </form.Field>
                )
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: '1 1 0px',
                  }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
        {NotificationComponent}
      </Paper>
    </Dialog>
  )
}
